/**
 * 信使对话二级页（05 开发文档 MessengerStage 全家桶 + contact-messenger.js 逐行移植）。
 * galgame 舞台：立绘居左 / 信纸浮右上（?from=letter 从天而降 0.7s）/ 对话窗横贯底部；
 * 歌词式台词（打字机 标点180ms/普通26ms、点窗跳过、最近 3 句堆）；信纸实时誊写
 * （预渲染行/誊写/擦除/（略））；题型自适应（text/email/url、textarea、chips 浮层、
 * 📎 附件）；跳过 / 改上一答回退栈 / 进度点 / 收尾确认区 / 三态投递。
 * 接线替换：SCRIPTS → contracts conversationScripts；假投递 → POST /public/contact-submissions
 * （附件经 presigned 直传，payload 只带 key）；Turnstile → 阿里云验证码 2.0（一期本地跳过）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  attachmentMaxBytes,
  conversationScripts,
  paperShortLabels,
  type ConversationStep,
  type ContactType,
} from '@chronospeak/contracts';
import { http } from '../lib/http';
import { pickZhEn, useLocale } from '../lib/locale';
import { SiteHeader } from '../components/SiteHeader';
import {
  captchaCreds,
  cleanupCaptchaDom,
  initCaptcha,
  installPopupShowFix,
  preloadCaptchaSdk,
  sealClickInit,
} from '../lib/captcha';
import messengerPortrait from '../assets/messenger.webp';

type Phase = 'intro' | 'chat' | 'preview' | 'sending' | 'success' | 'error';

/** 已完成问答（回退栈条目） */
interface HistoryEntry {
  index: number;
  answer: string;
}

/** 台词堆条目（歌词堆：只保留信使话术，最近 3 句） */
interface LyricLine {
  id: number;
  text: string;
  kind: 'say' | 'warn' | 'muted';
}

interface UploadedAttachment {
  stepKey: string;
  fileName: string;
  assetKey: string;
}

/**
 * 附件上传进度状态（2026-09-20 新需求，05 模块《附件上传进度指示_实现说明》）：
 * uploading boolean → 结构化状态。单附件题一次一个文件，无需多轨。
 * phase：presign（签名往返，通常 <1s）→ uploading（XHR onprogress 真实字节）
 * → retrying（重签归零重走）→ done/failed（终态，UI 定格/失败话术）。
 */
interface UploadState {
  phase: 'idle' | 'presign' | 'uploading' | 'retrying' | 'done' | 'failed';
  progress: number; // 0-100
  fileName: string;
}

export function MessengerPage({ scene }: { scene: ContactType }) {
  const { locale } = useLocale();
  const [searchParams] = useSearchParams();
  const script = conversationScripts[scene];
  const labels = paperShortLabels[scene];

  const T = useCallback((zh: string, en: string) => (locale === 'zh-CN' ? zh : en), [locale]);

  /* ---------------- 状态机（原型 answers/stepIndex/phase/history 同构） ---------------- */
  const [phase, setPhase] = useState<Phase>('intro');
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [lyrics, setLyrics] = useState<LyricLine[]>([]);
  const [typing, setTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [choicesOpen, setChoicesOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>({ phase: 'idle', progress: 0, fileName: '' });
  const uploading = uploadState.phase !== 'idle' && uploadState.phase !== 'done' && uploadState.phase !== 'failed';
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  // 附件镜像 ref：直传完成 → setAttachments(异步渲染) + submitAnswer()（同闭包旧 state）
  // 会读到空附件列表报「空着可寄不出去」——动画热路径同规则，提交读 ref 镜像
  const attachmentsRef = useRef<UploadedAttachment[]>([]);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  const lyricId = useRef(0);
  const typeTimer = useRef<number | null>(null);
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingFile = useRef<File | null>(null);

  const reduced = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );
  const fromLetter = searchParams.get('from') === 'letter';

  /* ---------------- 信纸降落（原型 sheet.drop-in：0.7s 落定） ---------------- */
  const [landed, setLanded] = useState(fromLetter ? false : true);
  useEffect(() => {
    if (fromLetter && !reduced) {
      const t = window.setTimeout(() => setLanded(true), 700);
      return () => window.clearTimeout(t);
    }
    setLanded(true);
  }, [fromLetter, reduced]);

  /* ---------------- 移动端键盘兜底（P1.5 三段式：dvh 在 iOS Safari 聚焦不缩） ----------------
     visualViewport 实时高度写 --kb-vh，聚焦收缩时给 .vn-page 挂 kb-open 收高——
     输入行 sticky 底栏随之贴在可见视口底（键盘上方），不被键盘盖住。 */
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.querySelector<HTMLElement>('.vn-page');
    const apply = () => {
      // 收缩超过 120px 视为键盘弹起（地址栏收起等小抖动不触发）
      const shrunk = window.innerHeight - vv.height > 120;
      root?.classList.toggle('kb-open', shrunk);
      if (shrunk) root?.style.setProperty('--kb-vh', `${vv.height}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      root?.classList.remove('kb-open');
    };
  }, []);

  /* ---------------- 打字机（原型 typeLine：标点 180ms / 普通 26ms；点窗跳过） ---------------- */
  const typeLine = useCallback(
    (text: string, kind: LyricLine['kind'] = 'say', onDone?: () => void) => {
      const id = ++lyricId.current;
      if (reduced) {
        // reduced-motion：整句直出（AGENTS §7 / 05 验收）
        setLyrics((prev) => trimTo([...prev, { id, text, kind }], 3));
        setTyping(false);
        onDone?.();
        return;
      }
      setTyping(true);
      let i = 0;
      const step = () => {
        if (i >= text.length) {
          setLyrics((prev) => {
            const next = trimTo(
              [...prev.filter((l) => l.id !== id), { id, text, kind }],
              3,
            );
            return next;
          });
          setTyping(false);
          onDone?.();
          return;
        }
        const ch = text[i] as string;
        i += 1;
        setLyrics((prev) => {
          const others = prev.filter((l) => l.id !== id);
          return trimTo([...others, { id, text: text.slice(0, i), kind }], 4);
        });
        typeTimer.current = window.setTimeout(step, '，。？！、：；'.includes(ch) ? 180 : 26);
      };
      typeTimer.current = window.setTimeout(step, 200);
    },
    [reduced],
  );

  const skipTyping = useCallback(() => {
    if (typeTimer.current !== null) {
      window.clearTimeout(typeTimer.current);
      typeTimer.current = null;
    }
    setLyrics((prev) =>
      prev.map((l, idx) => (idx === prev.length - 1 ? l : l)),
    );
    setTyping(false);
    // 跳过后直接进入输入呈现（onDone 语义）
    pendingDoneRef.current?.();
  }, []);
  const pendingDoneRef = useRef<(() => void) | null>(null);

  useEffect(() => () => {
    if (typeTimer.current !== null) window.clearTimeout(typeTimer.current);
  }, []);

  /** 歌词堆裁剪（原型 trimLyrics：保留最近 keep 句） */
  function trimTo(lines: LyricLine[], keep: number): LyricLine[] {
    return lines.slice(Math.max(0, lines.length - keep));
  }

  /** 追加即时句（警告/静默，原型 sayNow） */
  const sayNow = useCallback((text: string, kind: LyricLine['kind'] = 'warn') => {
    setLyrics((prev) => trimTo([...prev, { id: ++lyricId.current, text, kind }], 4));
  }, []);

  /* ---------------- 流程：开场 → askStep → 收尾（原型时序原样） ---------------- */
  useEffect(() => {
    // 埋点：对话开始（进入页面即计；09 冻结事件）
    void import('../lib/locale').then(({ track }) =>
      track('contact_letter_started', { contactType: scene, locale }),
    );
  }, [scene, locale]);

  const askStep = useCallback(
    (i: number) => {
      const step = script.steps[i];
      if (!step) return;
      setStepIndex(i);
      setPhase('chat');
      setInputValue('');
      setChoicesOpen(false);
      pendingDoneRef.current = () => {
        // 打完字后的输入呈现（原型 presentInput：chips 先出选项；聚焦输入行）
        if (step.type === 'chips' && step.chips) setChoicesOpen(true);
        if (!reduced) fieldRef.current?.focus();
      };
      typeLine(pickZhEn(step.q, locale), 'say', () => {
        if (step.type === 'chips' && step.chips) setChoicesOpen(true);
        pendingDoneRef.current = null;
        if (!reduced) fieldRef.current?.focus();
      });
    },
    [locale, reduced, script.steps, typeLine],
  );

  useEffect(() => {
    // 开场：intro 打完 → 第一问（320ms 间隔，原型时序）
    pendingDoneRef.current = null;
    typeLine(pickZhEn(script.intro, locale), 'say', () => {
      window.setTimeout(() => askStep(0), reduced ? 0 : 320);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- 校验（原型 validate：错误由信使说出来） ---------------- */
  const validate = (step: ConversationStep, value: string): string | null => {
    const v = value.trim();
    if (!v) return step.required ? T('这一项空着可寄不出去。', "This one can't go out empty.") : null;
    if (step.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return step.invalid ? pickZhEn(step.invalid, locale) : null;
    if (step.type === 'url' || step.type === 'file') {
      try {
        new URL(v.includes('://') ? v : 'https://' + v);
      } catch {
        return step.invalid ? pickZhEn(step.invalid, locale) : null;
      }
    }
    return null;
  };

  /* ---------------- 提交当前答案（原型 row.submit 逻辑） ---------------- */
  const submitAnswer = async (override?: string) => {
    if (phase !== 'chat') return;
    const step = script.steps[stepIndex];
    if (!step) return;
    // 原型 1:1：chips 点击路径是「field.value=chip + requestSubmit」同步序列。
    // React 受控组件 setState 在事件处理后才渲染——submitAnswer 读 state/DOM 都还是
    // 旧值（首次点 chip 报「空着可寄不出去」）。显式 override（chip 文本）同步直达，
    // 等价原型的 field.value= 写入；键盘路径仍读输入框实时 DOM 值。
    const value = (override ?? fieldRef.current?.value ?? inputValue).trim();
    // 读 ref 镜像（同闭包 state 在直传完成→提交路径上是旧值——§6.12 元规则）
    const attachment = attachmentsRef.current.find((a) => a.stepKey === step.key);
    const hasFile = step.type === 'file' && Boolean(attachment);
    const err = hasFile ? null : validate(step, value);
    if (err) {
      sayNow('✎ ' + err, 'warn');
      fieldRef.current?.focus();
      return;
    }
    const answer = hasFile
      ? '📎 ' + attachment!.fileName + (value ? T('　+ 备注：', ' + note: ') + value : '')
      : value;
    const next = { ...answers, [step.key]: answer };
    setAnswers(next);
    setHistory((h) => [...h, { index: stepIndex, answer }]);
    setChoicesOpen(false);
    const nextIndex = stepIndex + 1;
    window.setTimeout(
      () => {
        if (nextIndex < script.steps.length) askStep(nextIndex);
        else showPreview();
      },
      reduced ? 0 : 380,
    );
  };

  const showPreview = useCallback(() => {
    setPhase('preview');
    setStepIndex(script.steps.length);
    typeLine(
      T(
        '信誊好了，都在右上角这张纸上。你过目，愿意的话就封缄寄出。',
        'Your letter is transcribed on the paper. Have a look — seal it if it reads right.',
      ),
      'say',
      () => {
        if (!reduced) document.querySelector<HTMLButtonElement>('[data-seal]')?.focus();
      },
    );
  }, [reduced, script.steps.length, T, typeLine]);

  /* ---------------- 附件：选中即 presign 直传（选中即提交，原型一致） ---------------- */
  // 2026-09-19：直传 PUT 加 30s 超时 + 失败自动重签一次（staging 实测跨国 OSS 首连 TLS 冷启动
  // 偶发 >15s 或被重置——无超时时页面停在「正在上传」无反馈；重试消化偶发抖动，第二次走热连接毫秒级）。
  // 2026-09-20（用户需求）：fetch → XHR（upload.onprogress 拿真实字节进度）——消除上传期间无反馈
  // 静默；重试进度归零重走；30s 超时/重签逻辑原样保留。
  const onFilePicked = async (file: File | undefined) => {
    const step = script.steps[stepIndex];
    if (!file || !step || phase !== 'chat' || step.type !== 'file') return;
    if (file.size > attachmentMaxBytes) {
      sayNow('✎ ' + T('附件超过 50MB 了，换个小一点的？', 'The attachment exceeds 50MB — try a smaller one?'), 'warn');
      return;
    }
    setUploadState({ phase: 'presign', progress: 0, fileName: file.name });
    const putOnce = (uploadUrl: string, uploadHeaders: Record<string, string> | undefined): Promise<void> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.timeout = 30_000;
        // 契约通用：presign 签发的额外头必须实发（当前 contact-attachment 为空；builder-photo 通道带 OSS ACL 头）
        for (const [k, v] of Object.entries(uploadHeaders ?? {})) xhr.setRequestHeader(k, v);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadState((s) => (s.phase === 'idle' ? s : { ...s, phase: 'uploading', progress }));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`upload ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('upload network'));
        xhr.ontimeout = () => reject(new Error('upload timeout'));
        xhr.send(file);
      });
    };
    try {
      let presign = await http.post<{
        key: string;
        uploadUrl: string;
        uploadHeaders?: Record<string, string>;
      }>(`/public/asset-upload`, {
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        purpose: 'contact-attachment',
      });
      try {
        await putOnce(presign.uploadUrl, presign.uploadHeaders);
      } catch {
        // 首传超时/网络抖动：重签一次（旧 key 作废成孤儿，资产清理任务统一回收）再传；
        // 进度归零重走 + 「重试中」话术（方案 §2.1）
        setUploadState((s) => ({ ...s, phase: 'retrying', progress: 0 }));
        presign = await http.post<{
          key: string;
          uploadUrl: string;
          uploadHeaders?: Record<string, string>;
        }>(`/public/asset-upload`, {
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          purpose: 'contact-attachment',
        });
        await putOnce(presign.uploadUrl, presign.uploadHeaders);
      }
      setAttachments((prev) => [
        ...prev.filter((a) => a.stepKey !== step.key),
        { stepKey: step.key, fileName: file.name, assetKey: presign.key },
      ]);
      pendingFile.current = null;
      // setAttachments 后镜像 ref 同步直写（effect 在渲染后才跑，等不到）——保证随后的提交读到
      attachmentsRef.current = [
        ...attachmentsRef.current.filter((a) => a.stepKey !== step.key),
        { stepKey: step.key, fileName: file.name, assetKey: presign.key },
      ];
      setUploadState({ phase: 'done', progress: 100, fileName: file.name });
      void submitAnswerRef.current?.();
    } catch {
      sayNow('✎ ' + T('附件没传上去，再试一次？内容都还在。', 'Upload failed — try again? Everything else is kept.'), 'warn');
      setUploadState({ phase: 'failed', progress: 0, fileName: file.name });
    }
  };

  const submitAnswerRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    submitAnswerRef.current = () => void submitAnswer();
  });

  /* ---------------- 跳过 / 改上一答（原型 skipBtn / undoBtn） ---------------- */
  const skip = () => {
    if (phase !== 'chat') return;
    setInputValue('');
    void submitAnswer();
  };

  const undo = () => {
    if (!history.length || phase !== 'chat') return;
    const last = history[history.length - 1];
    if (!last) return;
    const undoneStep = script.steps[last.index];
    if (!undoneStep) return;
    setHistory((h) => h.slice(0, -1));
    setAnswers((a) => {
      const next = { ...a };
      delete next[undoneStep.key];
      return next;
    });
    setAttachments((prev) => prev.filter((a) => a.stepKey !== undoneStep.key));
    askStep(last.index);
  };

  /* ---------------- 封缄投递（三态；失败保留全部答案可重试） ---------------- */
  // 验证码（2026-09-18 阿里云 2.0 V2 架构）：sendLetter 带票据参数——正常路径票据由
  // SDK captchaVerifyCallback 传入；旁路（本地 dev / SDK 加载失败）传空串，
  // 服务端 CAPTCHA_VERIFY_DISABLED=true 跳过（staging/prod 空票据被 fail-closed 401 拦下）。
  const sendLetter = async (captchaVerifyParam = '') => {
    if (!consent) {
      sayNow(
        '✎ ' +
          T('还差一个勾：隐私说明要同意，信使才敢带走。', "One tick missing — I can't carry the letter without the privacy consent."),
        'warn',
      );
      return;
    }
    setPhase('sending');
    setLyrics([]);
    typeLine(T('封缄了。信使正在路上……', 'Sealed. The messenger is on the way…'), 'muted');
    try {
      // multipart 提交（端点 #8）：payload + {key}AssetKey + captchaTicket + honeypot
      const form = new FormData();
      form.append('type', scene);
      form.append('locale', locale);
      form.append('payload', JSON.stringify(answers));
      form.append('captchaTicket', captchaVerifyParam); // SDK 票据原样透传（官方禁止修改）
      form.append('website', ''); // honeypot：正常用户永远留空
      for (const att of attachmentsRef.current) {
        form.append(`${att.stepKey}AssetKey`, att.assetKey);
      }
      const res = await fetch('/api/v1/public/contact-submissions', { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `submit ${res.status}`);
      }
      setPhase('success');
      void import('../lib/locale').then(({ track }) =>
        track('contact_letter_completed', { contactType: scene, locale }),
      );
      return true;
    } catch (e) {
      setPhase('error');
      setErrorMsg(e instanceof Error ? e.message : 'submit failed');
      void import('../lib/locale').then(({ track }) =>
        track('contact_letter_failed', { contactType: scene, reason: 'network', locale }),
      );
      return false;
    }
  };
  const sendLetterRef = useRef(sendLetter);
  useEffect(() => {
    sendLetterRef.current = sendLetter;
  });

  /* ---------------- 验证码接线（阿里云 2.0 V2，官方 React Demo 模式） ----------------
   * mount 即 init（官方：初始化前置加载，与验证请求间隔 >2s 利于资源预载）。
   * onVerify：滑块通过 → sendLetter(票据)；返回成功与否给 SDK（false → SDK 重置滑块）。
   * 封缄按钮：旁路模式（captchaCreds() 为 null，本地 dev）→ 直接 sendLetter('')；
   * 正常模式 → captchaTriggerClick（SDK 挂在隐藏触发元素上的 click 监听弹滑块）；
   * SDK 加载/初始化失败（captchaBroken）→ 触发函数返回 false → 旁路 sendLetter('')，
   * 服务端 fail-closed 拒空票据（staging/prod 401 提示重试），本地 dev Disabled 照常过。 */
  const CAPTCHA_ELEMENT_ID = 'captcha-element';
  // 2026-09-19：SDK 触发元素 = 封缄按钮本身（官方 Demo 模式：button 参数指向业务提交按钮，
  // 用户真实点击（trusted event）被 SDK 接管弹滑块 → 滑块通过 → captchaVerifyCallback 提交）。
  // 此前用隐藏元素 + 程序化 el.click()（isTrusted=false）——SDK 挂了监听但不响应非可信点击，滑块永不弹出。
  const CAPTCHA_TRIGGER_ID = 'captcha-seal-btn';
  useEffect(() => {
    preloadCaptchaSdk(); // JS 前置加载（官方：与验证请求间隔 >2s）
    // 弹窗显示接管（无痕+二次挑战形态的 SDK 显示死锁 workaround；一点即过形态无副作用——armed
    // 只在封缄点击后生效）。与 init 解耦，mount 装一次（内部 document 委托，不依赖按钮存在）。
    installPopupShowFix(CAPTCHA_TRIGGER_ID);
    return cleanupCaptchaDom; // 官方 Demo：防重复 mount 多次回调
  }, [locale]);

  // init 提前到 preview 态渲染稳定后（2026-09-21 定稿，推翻 9/19 惰性 init——点击瞬间 init 与
  // React setPhase 渲染竞态，主线程同步卡死，生产实测 + CDP 复现；独立按钮 init 正常）。
  // preview 出现时按钮已挂载且状态稳定，SDK 安心绑定；点击时 sealClickInit 只做幂等兜底。
  useEffect(() => {
    if (phase !== 'preview') return;
    initCaptcha({
      elementId: CAPTCHA_ELEMENT_ID,
      triggerId: CAPTCHA_TRIGGER_ID,
      language: locale === 'zh-CN' ? 'cn' : 'en',
      onVerify: (param) => sendLetterRef.current(param).then((ok) => ok ?? false),
    });
  }, [phase, locale]);

  /** 封缄按钮统一入口：SDK 已接管（ready）时本函数不被调用场景不存在——React onClick 与 SDK 监听
   * 同时触发：SDK 弹滑块由回调提交；此处若 ready 则直接 return（避免双发）；未 ready（dev 旁路 /
   * SDK 加载中/失败）→ sendLetter('')。SDK 就绪前的短暂窗口点击会走旁路被服务端 401 拒（提示重试），
   * 可接受（初始化在 preview 即开始，正常浏览到可点击时早已就绪）。 */
  const sealAndSend = () => {
    if (!captchaCreds()) {
      void sendLetter(''); // 旁路（本地 dev / 构建未注入凭据）
      return;
    }
    // 隐私同意前置校验（2026-09-20 生产反馈：未勾同意也能点封缄 → SDK 走完验证后 sendLetter
    // 才拦，但 phase 已被置 sending 且无人恢复 → 卡死在「信使在路上」）。必须在这里拦下。
    if (!consent) {
      sayNow(
        '✎ ' +
          T('还差一个勾：隐私说明要同意，信使才敢带走。', "One tick missing — I can't carry the letter without the privacy consent."),
        'warn',
      );
      return;
    }
    // CHECK_BOX（一点即过）：sealClickInit 触发 init（首次）→ SDK 自动验证 → 回调提交。
    // 回调是异步的：这里先把 phase 推到 sending 给用户反馈，成败由 sendLetter 三态接手。
    if (sealClickInit({
      elementId: CAPTCHA_ELEMENT_ID,
      triggerId: CAPTCHA_TRIGGER_ID,
      language: locale === 'zh-CN' ? 'cn' : 'en',
      onVerify: (param) => sendLetterRef.current(param).then((ok) => ok ?? false),
    })) {
      setPhase('sending');
      setLyrics([]);
      typeLine(T('封缄了。信使正在路上……', 'Sealed. The messenger is on the way…'), 'muted');
      return;
    }
    sayNow('✎ ' + T('人机验证暂时不可用，直接为你寄出。', 'Human check unavailable — sending directly.'), 'muted');
    void sendLetter(''); // SDK 不可用降级
  };

  const currentStep = script.steps[Math.min(stepIndex, script.steps.length - 1)];
  const inputDisabled = typing || phase !== 'chat' || uploading;

  return (
    <main className='page vn-page' data-messenger={scene}>
      <SiteHeader />
      {/* 原型此页无 H1（axe page-has-heading-one）——补视觉隐藏 H1，视觉 1:1 语义达标（M4 a11y） */}
      <h1 className='visually-hidden'>
        {locale === 'zh-CN'
          ? scene === 'creators'
            ? '创作者联络'
            : '合作伙伴联络'
          : scene === 'creators'
            ? 'Contact for creators'
            : 'Contact for partners'}
      </h1>
      <header className='page-intro form-intro'>
        <p className='eyebrow'>
          <Link className='form-crumb' to={`/${locale}/contact`}>
            ← {locale === 'zh-CN' ? '联络站' : 'Contact Station'}
          </Link>{' '}
          · {scene === 'creators' ? 'For Creators' : 'For Partners'}
        </p>
      </header>
      <section className='vn-theater'>
        <img
          className='vn-portrait'
          src={messengerPortrait}
          alt={locale === 'zh-CN' ? '镇上的信使：手捧火漆信的黏土小人' : 'The town messenger: a clay figurine holding a wax-sealed letter'}
        />
        <div className={`letter-drop-wrap letter-float${!landed ? ' letter-sheet-drop' : ''}`}>
          <div className={`letter-sheet${landed ? ' is-landed' : ' drop-in'}`} data-sheet>
            <p className='paper-kind'>{pickZhEn(script.paper, locale)}</p>
            <p className='sheet-head'>
              {scene === 'creators'
                ? T('把你的创作递给小镇', 'Bring your practice to the town')
                : T('把合作的事说给小镇', 'Tell the town about your collaboration')}
            </p>
            <dl className='paper-lines' data-paper>
              {script.steps.map((s) => {
                const att = attachments.find((a) => a.stepKey === s.key);
                // 附件上传中 → 该行实时显示进度（方案 §2.2：文件名 + 百分比 + 细金线进度条）
                const isUploadingRow =
                  s.key === script.steps[stepIndex]?.key && s.type === 'file' && uploading;
                const value = answers[s.key];
                const label = labels[s.key];
                return (
                  <div className='paper-row' key={s.key} data-row={s.key}>
                    <dt>{label ? pickZhEn(label, locale) : s.key}</dt>
                    <dd
                      className={
                        !value && !att && !isUploadingRow
                          ? 'paper-blank'
                          : value
                            ? 'paper-ink'
                            : att || isUploadingRow
                              ? 'paper-ink'
                              : 'paper-skip'
                      }
                    >
                      {value
                        ? value
                        : att
                          ? '📎 ' + att.fileName
                          : isUploadingRow
                            ? (
                              <>
                                {'📎 ' + uploadState.fileName + ' · '}
                                {uploadState.phase === 'presign'
                                  ? T('准备上传…', 'Preparing upload…')
                                  : uploadState.phase === 'retrying'
                                    ? T(`重试中 (第 2 次)…`, 'Retrying (attempt 2)…')
                                    : T('上传中 ' + uploadState.progress + '%', 'Uploading ' + uploadState.progress + '%')}
                                {uploadState.phase === 'uploading' && (
                                  <span
                                    className='upload-progress-bar'
                                    role='progressbar'
                                    aria-valuenow={uploadState.progress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-label={T('附件上传进度', 'Attachment upload progress')}
                                  >
                                    <i style={{ width: uploadState.progress + '%' }} />
                                  </span>
                                )}
                              </>
                            )
                          : history.some((h) => script.steps[h.index]?.key === s.key)
                            ? T('（略）', '—')
                            : ''}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>

        {/* galgame 舞台：歌词浮层 + 对话窗 + 进度 */}
        <div className='messenger-stage' data-messenger-root>
          <section className='vn-stage' aria-label={T('信使对话', 'Messenger conversation')}>
            <div className='vn-choices' data-choices role='group' aria-label={T('选项', 'Choices')} hidden={!choicesOpen}>
              {phase === 'chat' && currentStep?.type === 'chips' && currentStep.chips?.map((c) => (
                <button
                  key={c.zh}
                  type='button'
                  data-chip={locale === 'zh-CN' ? c.zh : c.en}
                  onClick={() => {
                    const v = locale === 'zh-CN' ? c.zh : c.en;
                    setInputValue(v);
                    void submitAnswer(v); // 显式传值（原型 field.value=chip + requestSubmit 同步语义）
                  }}
                >
                  {locale === 'zh-CN' ? c.zh : c.en}
                </button>
              ))}
            </div>
            <div className='vn-lyrics' data-lyrics aria-live='polite'>
              <span className='vn-nameplate'>{T('镇上的信使', 'The Town Messenger')}</span>
              <div className='vn-text' data-text>
                {lyrics.map((l, i) => (
                  <p
                    key={l.id}
                    className={
                      (l.kind === 'warn' ? 'vn-warn' : l.kind === 'muted' ? 'vn-muted' : '') +
                      (i < lyrics.length - 1 ? ' vn-old' : '')
                    }
                  >
                    {l.text}
                  </p>
                ))}
              </div>
              <div className='vn-confirm' data-confirm hidden={phase !== 'preview' && phase !== 'sending' && phase !== 'success' && phase !== 'error'}>
                {phase === 'preview' && (
                  <label className='sheet-consent'>
                    <input type='checkbox' data-consent checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                    <span>
                      {T('我已阅读并同意', 'I have read and agree to the')}{' '}
                      <Link to={`/${locale}/privacy`}>{T('隐私说明', 'privacy notice')}</Link>
                    </span>
                  </label>
                )}
                {/* 封缄按钮常驻（SDK init 需要它挂载且可测量）；非 preview 态视觉隐藏
                    （vn-seal-off：移出流外透明 1px，不用 hidden/display:none——SDK 绑定要求可测量），
                    error 态由「重试封缄」承担重试入口，避免双按钮。 */}
                <button
                  className={`button primary${phase === 'preview' ? '' : ' vn-seal-off'}`}
                  type='button'
                  data-seal
                  id='captcha-seal-btn'
                  onClick={sealAndSend}
                >
                  {T('封缄寄出', 'Seal & send')}
                </button>
                {phase === 'sending' && (
                  <p className='vn-ok'>{T('信使正在路上……', 'The messenger is on the way…')}</p>
                )}
                {phase === 'success' && (
                  <>
                    <p className='vn-ok'>
                      <b>{T('信已抵达小镇', 'Your letter has arrived')}</b>{' '}
                      {T('谢谢你的来信，我们会尽快回复。', "Thank you — we'll be in touch soon.")}
                    </p>
                    <div className='vn-actions'>
                      <Link className='button' to={`/${locale}`}>
                        {T('回到小镇', 'Back to Town')}
                      </Link>
                      <Link className='button' to={`/${locale}/contact`}>
                        {T('再写一封', 'Write another')}
                      </Link>
                    </div>
                  </>
                )}
                {phase === 'error' && (
                  <>
                    <p className='vn-ok'>
                      <b>{T('信没能送出', 'The letter could not be sent')}</b>{' '}
                      {T('答案都还留在信纸上，稍后再试一次。', 'Your answers are still on the paper — try again shortly.')}
                      {errorMsg && <code style={{ display: 'block', fontSize: 12, marginTop: 4 }}>{errorMsg}</code>}
                    </p>
                    <div className='vn-actions'>
                      <button
                        className='button primary'
                        type='button'
                        onClick={() => {
                          setPhase('preview');
                        }}
                      >
                        {T('重试封缄', 'Retry sealing')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className='vn-window' data-window onClick={() => typing && skipTyping()}>
              <form
                className='vn-inputrow'
                data-inputrow
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitAnswer();
                }}
              >
                <input
                  type='file'
                  data-file
                  hidden
                  aria-hidden='true'
                  tabIndex={-1}
                  ref={fileInputRef}
                  accept={currentStep?.accept?.join(',')}
                  onChange={(e) => void onFilePicked(e.target.files?.[0])}
                />
                <button
                  className='vn-attach'
                  type='button'
                  data-attach
                  hidden={phase !== 'chat' || currentStep?.type !== 'file'}
                  aria-label={T('上传附件', 'Attach a file')}
                  disabled={inputDisabled}
                  onClick={() => fileInputRef.current?.click()}
                >
                  📎
                </button>
                <textarea
                  className='vn-field'
                  data-field
                  rows={currentStep?.type === 'textarea' ? 3 : 1}
                  autoComplete='off'
                  ref={fieldRef}
                  value={inputValue}
                  disabled={inputDisabled}
                  placeholder={
                    uploading
                      ? // 上传期间旁注（方案 §2.2-2）：替代无解释灰显
                        uploadState.phase === 'presign'
                        ? T('正在上传附件……', 'Uploading attachment…')
                        : uploadState.phase === 'retrying'
                          ? T('正在上传附件……重试中 (第 2 次)', 'Uploading attachment… retrying (attempt 2)')
                          : T(`正在上传附件……${uploadState.progress}%`, `Uploading attachment… ${uploadState.progress}%`)
                      : currentStep?.ph
                        ? locale === 'zh-CN'
                          ? currentStep.ph.zh
                          : currentStep.ph.en
                        : ''
                  }
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    // 单行输入 Enter 提交；textarea 换行（原型 input/textarea 分支）
                    if (e.key === 'Enter' && currentStep?.type !== 'textarea' && !e.shiftKey) {
                      e.preventDefault();
                      void submitAnswer();
                    }
                  }}
                />
                <button className='vn-send' type='submit' aria-label={T('发送', 'Send')} disabled={inputDisabled}>
                  →
                </button>
                <button
                  className='vn-skip'
                  type='button'
                  data-skip
                  hidden={!(phase === 'chat' && (currentStep?.skippable || !currentStep?.required))}
                  disabled={inputDisabled}
                  onClick={skip}
                >
                  {T('跳过', 'Skip')}
                </button>
              </form>
              <span className='vn-skip-hint' data-skiphint hidden={!typing}>
                {T('点击窗口跳过打字', 'Click to skip typing')}
              </span>
              <span className='vn-next' data-next hidden={!typing} aria-hidden='true'>
                ▼
              </span>
            </div>

            <footer className='vn-foot'>
              <nav className='vn-progress' data-progress aria-label={T('进度', 'Progress')}>
                {script.steps.map((s, i) => (
                  <i
                    key={s.key}
                    className={i < stepIndex ? 'is-done' : i === stepIndex && phase === 'chat' ? 'is-now' : ''}
                  />
                ))}
              </nav>
              <button
                className='vn-undo'
                type='button'
                data-undo
                hidden={!history.length || phase !== 'chat'}
                onClick={undo}
              >
                {T('改上一答', 'Edit previous')}
              </button>
            </footer>
          </section>
        </div>
      </section>
      <noscript>
        <p className='form-noscript'>
          {T('这封信需要 JavaScript 才能写。请在浏览器中启用 JavaScript。', 'This letter needs JavaScript. Please enable it in your browser.')}
        </p>
      </noscript>
      {/* 阿里云验证码 2.0（V2 架构）渲染容器：SDK 的 element 参数（弹层几何计算基准）。
          触发器 = 封缄按钮本身（captcha-seal-btn）。容器必须可测量（非 0 尺寸）——
          0×0 容器会让 SDK 弹窗的显示切换失效（真机实测：popup DOM 创建但 hidden 不解除）；
          视觉移出屏幕（left:-9999px）+ 正常尺寸，与官方 Demo 的可见占位元素等价。 */}
      <div
        id='captcha-element'
        aria-hidden='true'
        style={{ position: 'fixed', left: -9999, top: 0, width: 320, height: 40 }}
      />
    </main>
  );
}
